import { createAction, createAsyncAction } from 'typesafe-actions';

import type {
  AccountAdjustment,
  AccountSettlement,
  AccountSettlementCreateData,
  LedgerItemEvent,
  LedgerItemRecreationBatch,
  LedgerItemRecreationBatchCreateData,
  PayableSummary,
  ReconciliationType,
} from '@evenfinancial/finance-client';

import type { PortalLedgerItem } from '@portal/api-models';
import type { PortalError } from '@portal/common';

import type { IsomorphicRequestPayload } from '../types';

import { PayableActionType } from './actionTypes';
import type {
  AccountAddAdjustmentsForm,
  CreateOneOffLedgerItemForm,
} from './types';

export interface DeletePayableRequestPayload extends IsomorphicRequestPayload {
  id: number;
}

export interface ignorePayableRequestPayload extends IsomorphicRequestPayload {
  id: number;
  reconciliationType: ReconciliationType;
}

export const payableDetailPage = createAction(
  PayableActionType.GET_DETAIL_PAGE
)<{ id: number }>();

export const payableRequestAction = createAsyncAction(
  PayableActionType.GET_ALL_REQUEST,
  PayableActionType.GET_ALL_SUCCESS,
  PayableActionType.GET_ALL_FAILURE
)<IsomorphicRequestPayload, PayableSummary[], Error>();

export const payableByIdRequestAction = createAsyncAction(
  PayableActionType.GET_BY_ID_REQUEST,
  PayableActionType.GET_BY_ID_SUCCESS,
  PayableActionType.GET_BY_ID_FAILURE
)<{ id: number } & IsomorphicRequestPayload, PortalLedgerItem, Error>();

export const payableIgnoreAction = createAsyncAction(
  PayableActionType.IGNORE_PAYABLE_REQUEST,
  PayableActionType.IGNORE_PAYABLE_SUCCESS,
  PayableActionType.IGNORE_PAYABLE_FAILURE
)<ignorePayableRequestPayload, any, Error>();

export const payableAdjustmentsRequestAction = createAsyncAction(
  PayableActionType.GET_ALL_ADJUSTMENTS_REQUEST,
  PayableActionType.GET_ALL_ADJUSTMENTS_SUCCESS,
  PayableActionType.GET_ALL_ADJUSTMENTS_FAILURE
)<IsomorphicRequestPayload, AccountAdjustment[], Error>();

export const payableAdjustmentsByLedgerItemIdRequestAction = createAsyncAction(
  PayableActionType.GET_ADJUSTMENTS_BY_LEDGER_ITEM_ID_REQUEST,
  PayableActionType.GET_ADJUSTMENTS_BY_LEDGER_ITEM_ID_SUCCESS,
  PayableActionType.GET_ADJUSTMENTS_BY_LEDGER_ITEM_ID_FAILURE
)<
  IsomorphicRequestPayload & { ledgerItemId: number },
  { adjustments: AccountAdjustment[]; ledgerItemId: number },
  Error
>();

export const payableAdjustmentDeleteAction = createAsyncAction(
  PayableActionType.DELETE_ADJUSTMENT_REQUEST,
  PayableActionType.DELETE_ADJUSTMENT_SUCCESS,
  PayableActionType.DELETE_ADJUSTMENT_FAILURE
)<{ id: number } & IsomorphicRequestPayload, any, Error>();

export const deletePayableAction = createAsyncAction(
  PayableActionType.DELETE_PAYABLE_REQUEST,
  PayableActionType.DELETE_PAYABLE_SUCCESS,
  PayableActionType.DELETE_PAYABLE_FAILURE
)<DeletePayableRequestPayload, any, Error>();

export const regenerateLedgerItemsRequestAction = createAsyncAction(
  PayableActionType.REGENERATE_LEDGER_ITEMS_REQUEST,
  PayableActionType.REGENERATE_LEDGER_ITEMS_SUCCESS,
  PayableActionType.REGENERATE_LEDGER_ITEMS_FAILURE
)<
  LedgerItemRecreationBatchCreateData & IsomorphicRequestPayload,
  LedgerItemRecreationBatch,
  Error
>();

export const settleLedgerItemsRequestAction = createAsyncAction(
  PayableActionType.SETTLE_LEDGER_ITEMS_REQUEST,
  PayableActionType.SETTLE_LEDGER_ITEMS_SUCCESS,
  PayableActionType.SETTLE_LEDGER_ITEMS_FAILURE
)<
  AccountSettlementCreateData & IsomorphicRequestPayload,
  AccountSettlement[],
  Error
>();

export const createOneOffLedgerItemRequestAction = createAsyncAction(
  PayableActionType.CREATE_ONE_OFF_LEDGER_ITEM_REQUEST,
  PayableActionType.CREATE_ONE_OFF_LEDGER_ITEM_SUCCESS,
  PayableActionType.CREATE_ONE_OFF_LEDGER_ITEM_FAILURE
)<
  CreateOneOffLedgerItemForm & IsomorphicRequestPayload,
  PortalLedgerItem,
  PortalError
>();

export const payableAddAdjustmentAction = createAsyncAction(
  PayableActionType.ADD_ADJUSTMENT_REQUEST,
  PayableActionType.ADD_ADJUSTMENT_SUCCESS,
  PayableActionType.ADD_ADJUSTMENT_FAILURE
)<
  AccountAddAdjustmentsForm & IsomorphicRequestPayload,
  AccountAdjustment[],
  Error
>();

export const toggleAddAdjustmentsForm = createAction(
  PayableActionType.TOGGLE_ADD_ADJUSTMENTS_FORM
)<{ accountId?: number; ledgerItemId?: number }>();

export const updateAddAdjustmentsForm = createAction(
  PayableActionType.UPDATE_ADD_ADJUSTMENTS_FORM
)<AccountAddAdjustmentsForm>();

export const toggleCreateOneOffLedgerItem = createAction(
  PayableActionType.TOGGLE_CREATE_ONE_OFF_LEDGER_ITEM
)<undefined>();

export const payableEventsByIdRequestAction = createAsyncAction(
  PayableActionType.GET_EVENTS_BY_ID_REQUEST,
  PayableActionType.GET_EVENTS_BY_ID_SUCCESS,
  PayableActionType.GET_EVENTS_BY_ID_FAILURE
)<{ id: number } & IsomorphicRequestPayload, LedgerItemEvent[], Error>();
